import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import PartyName from "./PartyName";
import { createQuoteRequests } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import {
  eligibleForQuoteRequest,
  isInFuture,
  requestCounts,
  shareFields,
  toIsoUtc,
  toLocalInput,
  validateShares,
} from "./placingUtils";
import NotesField from "./NotesField";

/**
 * Broker: send quote requests (step 9), one per party, to leads that have
 * declared interest and have no open quote request. `defaultPartyIds`
 * pre-selects particular leads (e.g. from a lead's row).
 */
const QuoteRequestForm = ({ submission, comparison, onSent, defaultPartyIds }) => {
  const toast = useToast();
  const [partyIds, setPartyIds] = useState([]);
  const [form, setForm] = useState({
    requestedShare: "",
    minimumShare: "",
    maximumShare: "",
    participationBasis: "",
    currency: "GBP",
    responseRequiredByDate: toLocalInput(submission.responseRequiredByDate),
    notes: "",
  });
  const [sending, setSending] = useState(false);

  const rows = comparison?.rows || [];
  const nominated = rows.some((r) => r.status === "nominated");
  // Every interested lead can be sent a quote request, including another one
  const eligible = useMemo(() => eligibleForQuoteRequest(submission.sharedWithParties), [submission.sharedWithParties]);
  const counts = requestCounts(rows);
  const eligibleKey = eligible.map((l) => l.partyId).join(",");
  const notYetRequestedKey = eligible.filter((l) => !counts[l.partyId]).map((l) => l.partyId).join(",");
  const defaultKey = (defaultPartyIds || []).join(",");

  // Default to the given leads, or the interested leads not sent one yet
  // (re-requesting is a deliberate choice)
  useEffect(() => {
    const ids = eligibleKey ? eligibleKey.split(",") : [];
    setPartyIds(
      defaultKey
        ? ids.filter((id) => defaultKey.split(",").includes(id))
        : notYetRequestedKey
        ? notYetRequestedKey.split(",")
        : []
    );
  }, [eligibleKey, notYetRequestedKey, defaultKey]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const toggle = (id) =>
    setPartyIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const send = async () => {
    const shareError = validateShares({
      share: form.requestedShare,
      minimumShare: form.minimumShare,
      maximumShare: form.maximumShare,
    });
    if (shareError) return toast({ title: shareError, status: "warning" });
    if (form.responseRequiredByDate && !isInFuture(form.responseRequiredByDate)) {
      return toast({ title: "Response date must be in the future", status: "warning" });
    }
    if (partyIds.length === 0) return toast({ title: "Choose at least one lead", status: "warning" });

    setSending(true);
    try {
      await createQuoteRequests(submission._id, {
        partyIds,
        ...shareFields({
          requestedShare: form.requestedShare,
          minimumShare: form.minimumShare,
          maximumShare: form.maximumShare,
        }),
        ...(form.notes.trim() && { notes: form.notes.trim() }),
        ...(form.participationBasis && { participationBasis: form.participationBasis }),
        ...(form.currency && { currency: form.currency }),
        ...(form.responseRequiredByDate && { responseRequiredByDate: toIsoUtc(form.responseRequiredByDate) }),
      });
      toast({ title: `Quote request sent to ${partyIds.length} lead(s)`, status: "success" });
      await onSent();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't send quote requests" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
      <Heading size="sm" mb={3}>Request quotes</Heading>
      {nominated ? (
        <Text fontSize="sm" color="gray.500">Leads have been nominated; no more quote requests.</Text>
      ) : eligible.length === 0 ? (
        <Text fontSize="sm" color="gray.500">No leads have declared interest yet.</Text>
      ) : (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Send to</Text>
            <HStack wrap="wrap" spacing={4}>
              {eligible.map((lead) => (
                <Checkbox key={lead.partyId} isChecked={partyIds.includes(lead.partyId)} onChange={() => toggle(lead.partyId)}>
                  <PartyName partyId={lead.partyId} />
                  {counts[lead.partyId] > 0 && (
                    <Text as="span" fontSize="xs" color="gray.500"> · {counts[lead.partyId]} request{counts[lead.partyId] > 1 ? "s" : ""} sent</Text>
                  )}
                </Checkbox>
              ))}
            </HStack>
          </Box>
          <SimpleGrid columns={[1, 3]} spacing={3}>
            <FormControl isRequired>
              <FormLabel fontSize="xs">Requested share %</FormLabel>
              <Input size="sm" type="number" min={0} max={100} step="any" value={form.requestedShare} onChange={set("requestedShare")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Minimum share %</FormLabel>
              <Input size="sm" type="number" min={0} max={100} step="any" value={form.minimumShare} onChange={set("minimumShare")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Maximum share %</FormLabel>
              <Input size="sm" type="number" min={0} max={100} step="any" value={form.maximumShare} onChange={set("maximumShare")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Participation basis</FormLabel>
              <Input size="sm" value={form.participationBasis} onChange={set("participationBasis")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Currency</FormLabel>
              <Input size="sm" value={form.currency} onChange={set("currency")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Response required by</FormLabel>
              <Input size="sm" type="datetime-local" value={form.responseRequiredByDate} onChange={set("responseRequiredByDate")} />
            </FormControl>
          </SimpleGrid>
          <NotesField
            label="Notes for the carriers"
            value={form.notes}
            onChange={(notes) => setForm((f) => ({ ...f, notes }))}
            placeholder="What you're asking them to quote on"
          />
          <Text fontSize="xs" color="gray.500">
            Every lead that has declared interest is listed; each one ticked is sent its own quote request.
          </Text>
          <HStack>
            <Button size="sm" colorScheme="blue" onClick={send} isLoading={sending} isDisabled={partyIds.length === 0}>
              Send quote request to {partyIds.length} lead(s)
            </Button>
          </HStack>
        </VStack>
      )}
    </Box>
  );
};

export default QuoteRequestForm;
